'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { motion } from 'framer-motion';
import { useState } from 'react';
const FloatingBox = ({ delay, duration, x, y }: { delay: number; duration: number; x: number; y: number }) => (
  <motion.div
    className="absolute w-2 h-2 bg-black rounded-full opacity-10"
    animate={{
      y: [0, y, 0],
      x: [0, x, 0],
      scale: [1, 1.2, 1],
    }}
    transition={{
      delay,
      duration,
      repeat: Infinity,
      ease: 'easeInOut',
    }}
  />
);

const Beam = ({ delay }: { delay: number }) => (
  <motion.div
    className="absolute h-0.5 bg-gradient-to-r from-transparent via-black to-transparent opacity-5"
    style={{ width: '200px', left: '50%', top: '50%', marginLeft: '-100px' }}
    animate={{
      rotate: [0, 360],
    }}
    transition={{
      delay,
      duration: 20,
      repeat: Infinity,
      ease: 'linear',
    }}
  />
);

const ARCHITECTURE_QUOTES = [
  '"Buildings are not finished. Only abandoned." - Louis Kahn',
  '"Space and light and order. Those are the things that men need as much as they need bread or a place to sleep." - Le Corbusier',
  '"Sustainability is just another word for good design." - Cameron Sinclair',
  '"A great building must begin with the unmeasurable, must go through measurable means when it is being designed, and in the end must be unmeasurable." - Louis Kahn',
  '"The space within becomes the reality of the building." - Frank Lloyd Wright',
  '"Architecture is for the open minded and for dreamers." - Zaha Hadid',
  '"Design is not just what it looks like. Design is how it works." - Steve Jobs',
  '"A chair is a very difficult object. A skyscraper is almost easier." - Ludwig Mies van der Rohe'
];

export default function NotFound() {
  const t = useTranslations('notFound');
  const [showQuote, setShowQuote] = useState(false);
  const [currentQuote, setCurrentQuote] = useState('');

  const handleArchitectureEasterEgg = () => {
    const randomQuote = ARCHITECTURE_QUOTES[Math.floor(Math.random() * ARCHITECTURE_QUOTES.length)];
    setCurrentQuote(randomQuote);
    setShowQuote(true);
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#F9F9F9] via-white to-[#F9F9F9] flex items-center justify-center px-6 overflow-hidden relative">
      {/* Animated background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <FloatingBox delay={0} duration={6} x={40} y={60} />
        <FloatingBox delay={0.5} duration={7} x={-50} y={70} />
        <FloatingBox delay={1} duration={8} x={60} y={-50} />
        <FloatingBox delay={1.5} duration={6.5} x={-30} y={-40} />
        <Beam delay={0} />
        <Beam delay={0.5} />
      </div>

      {/* Content Container */}
      <div className="relative z-10 max-w-2xl w-full">
        {/* Animated 404 Visualization */}
        <motion.div
          className="flex justify-center mb-12 h-40"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="relative w-40 h-40 flex items-center justify-center">
            {/* Main 404 Display */}
            <div className="text-center">
              <motion.div
                className="flex justify-center items-baseline gap-2 mb-4"
              >
                <motion.span
                  className="text-7xl font-bold text-black"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  4
                </motion.span>
                <motion.div
                  className="w-12 h-12 border-2 border-black rounded-full flex items-center justify-center"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                >
                  <motion.div
                    className="w-2 h-2 bg-black rounded-full"
                    animate={{ scale: [1, 1.5, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </motion.div>
                <motion.span
                  className="text-7xl font-bold text-black"
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, delay: 0.2 }}
                >
                  4
                </motion.span>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Text Content */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-black mb-4 tracking-tight">
            {t('title')}
          </h1>
          <p className="text-lg text-[#888888] max-w-md mx-auto">
            {t('description')}
          </p>
        </motion.div>

        {/* Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Link href="/">
            <button className="px-8 py-3 bg-black text-white font-medium text-sm hover:bg-black/90 transition-colors cursor-pointer">
              {t('backHome')}
            </button>
          </Link>
          <button
            onClick={handleArchitectureEasterEgg}
            className="px-8 py-3 bg-black text-white font-medium text-sm hover:bg-black/90 transition-colors cursor-pointer"
          >
            {t('inspiration')}
          </button>
        </motion.div>

        {/* Architecture Quote Easter Egg */}
        {showQuote && (
          <motion.div
            className="mt-12 p-6 border-l-4 border-black bg-white max-w-md mx-auto"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <p className="text-sm text-[#333333] italic">{currentQuote}</p>
          </motion.div>
        )}
      </div>
    </main>
  );
}
